import React, { useState, useEffect } from 'react';
import axios from 'axios';
import OpsGraphInstallations from './OpsGraphInstallations';
import { Users, CalendarCheck, Loader, CircleCheck, AlertCircle, Sun, Thermometer, ChevronLeft, ChevronRight, Image } from 'lucide-react';

const isoLocal = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const API = process.env.REACT_APP_BACKEND_URL || '';

const COULEUR_ETAPE = {
  planifie: '#9CA3AF', arrivee: '#3B82F6', previsite: '#6366F1',
  installation: '#F59E0B', codes_barres: '#F59E0B', config_app: '#8B5CF6',
  photos_apres: '#06B6D4', documents: '#14B8A6', termine: '#10B981'
};

const aujourdhui = () => isoLocal(new Date());

const decaler = (iso, jours) => {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + jours);
  return isoLocal(d);
};

const OpsDashboard = () => {
  const [data, setData] = useState(null);
  const [jour, setJour] = useState(aujourdhui());
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    setChargement(true);
    axios.get(`${API}/api/chantiers/dashboard/resume`, { params: { date: jour } })
      .then((r) => setData(r.data.data))
      .catch(() => setData(null))
      .finally(() => setChargement(false));
  }, [jour]);

  const carte = { background: '#fff', borderRadius: '12px', padding: '18px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };
  const libelle = { display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', color: '#6B7280', marginBottom: '8px' };
  const valeur = { fontSize: '26px', fontWeight: 600, color: '#111827' };
  const bientot = { fontSize: '13px', color: '#9CA3AF', paddingTop: '6px' };

  const chantiers = data?.chantiers_du_jour || [];

  return (
    <div style={{ padding: '8px' }}>
      {/* Ligne 1 : cycle de vie du chantier */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        <div style={carte}>
          <div style={libelle}><Users size={15} color="#10B981" /> Équipes actives</div>
          <div style={valeur}>
            {data?.equipes_actives ?? '—'}
            <span style={{ fontSize: '15px', color: '#9CA3AF', fontWeight: 400 }}> / {data?.equipes_total ?? '—'}</span>
          </div>
        </div>

        <div style={carte}>
          <div style={libelle}><CalendarCheck size={15} color="#10B981" /> Chantiers programmés</div>
          <div style={valeur}>{data?.chantiers_programmes ?? '—'}</div>
        </div>

        <div style={carte}>
          <div style={libelle}><Loader size={15} color="#10B981" /> Chantiers en cours</div>
          <div style={valeur}>{data?.chantiers_en_cours ?? '—'}</div>
        </div>

        <div style={carte}>
          <div style={libelle}><CircleCheck size={15} color="#10B981" /> Chantiers terminés</div>
          <div style={valeur}>{data?.chantiers_termines_mois ?? '—'}</div>
          <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>ce mois-ci</div>
        </div>
      </div>

      {/* Ligne 2 : chantiers du jour */}
      <div style={{ ...carte, marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
          <span style={{ fontSize: '16px', fontWeight: 600 }}>Chantiers du jour</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={() => setJour(decaler(jour, -1))}
              style={{ padding: '6px 10px', border: '1px solid #D1D5DB', background: '#fff', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <ChevronLeft size={15} />
            </button>
            <input type="date" value={jour} onChange={(e) => setJour(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }} />
            <button onClick={() => setJour(decaler(jour, 1))}
              style={{ padding: '6px 10px', border: '1px solid #D1D5DB', background: '#fff', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <ChevronRight size={15} />
            </button>
            {jour !== aujourdhui() && (
              <button onClick={() => setJour(aujourdhui())}
                style={{ padding: '7px 12px', border: 'none', background: '#ECFDF5', color: '#065F46', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
                Aujourd'hui
              </button>
            )}
          </div>
        </div>

        {chargement ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#9CA3AF' }}>Chargement…</div>
        ) : chantiers.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#9CA3AF' }}>Aucun chantier ce jour-là</div>
        ) : chantiers.map((c, i) => (
          <div key={c.id} style={{ borderBottom: i < chantiers.length - 1 ? '1px solid #F3F4F6' : 'none', paddingBottom: '16px', marginBottom: i < chantiers.length - 1 ? '16px' : 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
              <span style={{ fontSize: '15px', fontWeight: 500 }}>
                {c.client ? `${c.client.prenom} ${c.client.nom}` : 'Client inconnu'}
              </span>
              <span style={{ fontSize: '11px', background: (COULEUR_ETAPE[c.etape] || '#9CA3AF') + '22', color: COULEUR_ETAPE[c.etape] || '#9CA3AF', padding: '3px 10px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                {c.etape_libelle}
              </span>
            </div>
            <div style={{ height: '6px', background: '#F3F4F6', borderRadius: '999px', overflow: 'hidden', marginBottom: '10px' }}>
              <div style={{ height: '100%', width: `${c.progression}%`, background: '#10B981', transition: 'width .3s' }} />
            </div>
            <div style={{ fontSize: '13px', color: '#6B7280', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span>{c.equipe?.nom || 'Sans équipe'}</span>
              <span>·</span>
              <span>{c.client?.ville} {c.client?.code_postal}</span>
              {c.puissance_kwc && <><span>·</span><span>{c.puissance_kwc} kWc</span></>}
              <span>·</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Image size={13} /> {c.nb_photos || 0} photo{(c.nb_photos || 0) > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Ligne 3 : SAV et stock */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '16px' }}>
        <div style={carte}>
          <div style={libelle}><AlertCircle size={15} color="#9CA3AF" /> SAV non réglés</div>
          <div style={bientot}>Bientôt</div>
        </div>
        <div style={carte}>
          <div style={libelle}><Sun size={15} color="#9CA3AF" /> Stock panneaux</div>
          <div style={bientot}>Bientôt</div>
        </div>
        <div style={carte}>
          <div style={libelle}><Thermometer size={15} color="#9CA3AF" /> Stock PAC</div>
          <div style={bientot}>Bientôt</div>
        </div>
      </div>

      <div style={{ marginTop: '16px' }}>
        <OpsGraphInstallations />
      </div>
    </div>
  );
};

export default OpsDashboard;
